import { Box, Tooltip } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { resamplingLabelIds } from '~/domain/patient/resamplingOrigin';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { useIsroImageLabel } from '~/hooks/workflows/isro/useIsroImageLabel';
import { useReplanningImageLabel } from '~/containers/viewer/explorer-sidebar/WorkflowSidebar/AdaptiveReplanningStep/useReplanningImageLabel';
import { referenceComparisonState } from '~/state/atoms/viewportGridAtoms';
import type { TileStatusContext } from '~/viewportGrid';

type DrawnImage = TileStatusContext['images'][number];

const MARK_SX = {
    fontSize: 11,
    whiteSpace: 'nowrap',
    flexShrink: 0,
} as const;

// One drawn image's comparison role, reference mark and resampling origin.
export default function TileImageMarksOfImage({
    image,
}: {
    image: DrawnImage;
}) {
    const intl = useIntl();
    const patient = useSelectedPatient();
    const reference = useRecoilValue(referenceComparisonState);
    const isroLabel = useIsroImageLabel(image.seriesInstanceUid);
    const replanningLabel = useReplanningImageLabel(image.seriesInstanceUid);
    const role = isroLabel ?? replanningLabel;
    const origin = patient?.images.find(
        (candidate) => candidate.seriesUid === image.seriesInstanceUid
    )?.resamplingOrigin;
    const isReference = reference?.seriesUid === image.seriesInstanceUid;

    return (
        <>
            {role && (
                <Box
                    component="span"
                    data-testid="tile-image-role"
                    sx={{ ...MARK_SX, color: 'secondary.main' }}
                >
                    <FormattedMessage id={role.id} values={role.values} />
                </Box>
            )}
            {isReference && (
                <Box
                    component="span"
                    data-testid="tile-reference-mark"
                    sx={{ ...MARK_SX, color: 'info.main' }}
                >
                    <FormattedMessage id="viewport.reference.readOnly" />
                </Box>
            )}
            {origin && (
                <Tooltip
                    title={intl.formatMessage({
                        id: resamplingLabelIds(origin).hint,
                    })}
                >
                    <Box
                        component="span"
                        data-testid="image-resampled-label"
                        sx={{ ...MARK_SX, color: 'warning.main' }}
                    >
                        <FormattedMessage
                            id={resamplingLabelIds(origin).label}
                        />
                    </Box>
                </Tooltip>
            )}
        </>
    );
}
